/**
 * Revont Pipelines - Blueprint + Strategy + Pipeline Types
 *
 * Mental model (loadout analogy):
 *   - Pipeline  : 1 thin row per org; holds activeBlueprintId.
 *   - Blueprint : a complete loadout (stages + gates + qualifications +
 *                 optional strategyId). orgId nullable: NULL = system default,
 *                 readable to every org, writable only by owning org.
 *   - Strategy  : a qualification framework (BANT/MEDDIC/etc). orgId nullable:
 *                 NULL = system default. Defines the qualification field
 *                 catalog, default stage placement, default gates, and the
 *                 slot-alias map used by the memory-to-qualifications
 *                 reconciler when no strategy-aware verdict is available.
 *
 * SQL columns are snake_case; TS types are camelCase. The authoritative
 * schema lives in `init-db/52-pipelines-schema.sql` and the system defaults
 * in `init-db/55-strategy-and-blueprint-seeds.sql`.
 */
import { z } from 'zod';
import { FORECAST_CATEGORIES } from './forecast.js';
import { CrmCloseOutcome } from './crm.js';
import { ActionItemActionType } from './post-meeting-analytics.js';
// =============================================================================
// Shared enums
// =============================================================================
export const QualificationValueTypeSchema = z.enum(['text', 'number', 'enum', 'list']);
export const GateActionTypeSchema = z.enum(Object.values(ActionItemActionType));
export const GatePrioritySchema = z.enum(['high', 'medium', 'low']);
export const QualificationCaptureSourceSchema = z.enum(['rep', 'ai_meeting', 'ai_email']);
// =============================================================================
// Strategy
// =============================================================================
// `qualificationsDefinition` mirrors the JSONB column shape: an ordered list
// of qualification fields the strategy contributes.
export const StrategyQualificationDefSchema = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(255),
    valueType: QualificationValueTypeSchema,
    enumValues: z.array(z.string()).optional(),
    placeholder: z.string().optional(),
});
export const StrategyGateDefSchema = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(255),
    actionType: GateActionTypeSchema.nullable(),
    priority: GatePrioritySchema,
});
export const StrategySchema = z.object({
    id: z.string().uuid(),
    /** NULL = system default, world-readable. */
    orgId: z.string().min(1).nullable(),
    key: z.string().min(1).max(64),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    qualificationsDefinition: z.array(StrategyQualificationDefSchema),
    /** {stageLabel: [qualificationKey]} */
    defaultStagePlacement: z.record(z.string(), z.array(z.string())),
    /** {stageLabel: [{key,label,actionType,priority}]} */
    defaultGates: z.record(z.string(), z.array(StrategyGateDefSchema)),
    /** {memorySlotKey: qualificationKey} - fallback for received-email reconciler */
    slotAliasMap: z.record(z.string(), z.string()),
    systemDefault: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// =============================================================================
// Blueprint
// =============================================================================
export const BlueprintSchema = z.object({
    id: z.string().uuid(),
    /** NULL = system default, world-readable. */
    orgId: z.string().min(1).nullable(),
    key: z.string().min(1).max(64),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    strategyId: z.string().uuid().nullable(),
    systemDefault: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// =============================================================================
// StageSubdivision
// =============================================================================
// Sub-columns within a stage (e.g. Won/Lost on a terminal stage). A
// subdivision tagged with an `outcome` ('won'|'lost') drives the existing
// `deals.close_*` columns; untagged subdivisions write the generic
// `deals.stage_*` columns. `reasons` is the per-subdivision prompt list shown
// when a deal moves into it.
export const StageSubdivisionSchema = z.object({
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(255),
    color: z.string().nullable().optional(),
    outcome: z.enum(Object.values(CrmCloseOutcome)).nullable().optional(),
    reasons: z.array(z.string().min(1)).default([]),
});
// =============================================================================
// BlueprintStage
// =============================================================================
export const BlueprintStageSchema = z.object({
    id: z.string().uuid(),
    blueprintId: z.string().uuid(),
    orgId: z.string().min(1).nullable(),
    /** Stable slug; written to `deals.stage`. */
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(255),
    sortOrder: z.number().int().nonnegative(),
    color: z.string().min(1),
    isTerminal: z.boolean(),
    /** When false, only a human can advance a deal INTO this stage. */
    aiCanAdvance: z.boolean(),
    /** Free-text "what this stage is about" for the suggestion prompt. */
    scopeIn: z.string().nullable(),
    /** Free-text "what this stage is NOT about" - guards against drift. */
    scopeOut: z.string().nullable(),
    /**
     * Optional admin override for the forecast bucket deals in this stage roll
     * into. Null/absent = auto-derive from the stage's position in the pipeline.
     */
    forecastCategory: z.enum(FORECAST_CATEGORIES).nullable().optional(),
    /**
     * Optional admin override for the default win probability (0-100) of deals in
     * this stage. Null/absent = auto-derive from the stage's position.
     */
    probability: z.number().int().min(0).max(100).nullable().optional(),
    /** Typical number of days deals spend in this stage until won-deal history exists. */
    typicalDurationDays: z.number().int().positive().optional(),
    /**
     * Sub-columns within this stage (e.g. Won/Lost). Empty array = no
     * subdivisions. Terminal stages carry outcome-tagged Won/Lost subdivisions.
     */
    subdivisions: z.array(StageSubdivisionSchema).default([]),
    /**
     * Stage-level reasons prompted when a deal moves into this stage (independent
     * of any per-subdivision reasons). Empty array = no prompt.
     */
    reasons: z.array(z.string().min(1)).default([]),
});
// =============================================================================
// BlueprintStageGate
// =============================================================================
export const BlueprintStageGateSchema = z.object({
    id: z.string().uuid(),
    blueprintStageId: z.string().uuid(),
    orgId: z.string().min(1).nullable(),
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(255),
    actionType: GateActionTypeSchema.nullable(),
    priority: GatePrioritySchema,
    sortOrder: z.number().int().nonnegative(),
    /** When false, AI may never auto-flip this gate. */
    aiCanComplete: z.boolean(),
    /** When true, an email matched to this gate (sent or received) flips it. */
    satisfiesOnEmail: z.boolean(),
    /** When false, the gate is informational only - does not block advancement. */
    isRequired: z.boolean(),
    /** Preserved through strategy swaps; non-blocking when true. */
    deprecatedByStrategySwitch: z.boolean(),
});
// =============================================================================
// BlueprintStageQualification
// =============================================================================
export const BlueprintStageQualificationSchema = z.object({
    id: z.string().uuid(),
    blueprintStageId: z.string().uuid(),
    orgId: z.string().min(1).nullable(),
    key: z.string().min(1).max(64),
    label: z.string().min(1).max(255),
    valueType: QualificationValueTypeSchema,
    enumValues: z.array(z.string()).nullable(),
    isRequired: z.boolean(),
    aiCanCapture: z.boolean(),
    sortOrder: z.number().int().nonnegative(),
    deprecatedByStrategySwitch: z.boolean(),
});
// =============================================================================
// Pipeline (thin slot, 1 per org)
// =============================================================================
export const PipelineSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    activeBlueprintId: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    closedDealRetentionDays: z.number().int().min(-1).optional(),
});
// =============================================================================
// DealQualificationValue
// =============================================================================
export const DealQualificationValueSchema = z.object({
    id: z.string().uuid(),
    dealId: z.string().uuid(),
    blueprintStageQualificationId: z.string().uuid(),
    orgId: z.string().min(1),
    /** Shape depends on the parent qualification's `valueType`. */
    value: z.unknown(),
    capturedBy: QualificationCaptureSourceSchema,
    capturedByUserId: z.string().uuid().nullable(),
    sourceMeetingId: z.string().uuid().nullable(),
    sourceEmailId: z.string().uuid().nullable(),
    /** Memory-first provenance. Non-empty for AI captures, may be empty for rep captures. */
    sourceMemoryFactIds: z.array(z.string().uuid()),
    /** True when AI confidence < threshold; rep must confirm before this counts toward advancement. */
    isAiPending: z.boolean(),
    /** 0.00-1.00; NULL for rep captures. */
    aiConfidence: z.number().min(0).max(1).nullable(),
    capturedAt: z.string().datetime(),
});
